import {
  introspectToken,
  refreshAuthentication,
  type LoginResult,
} from '@/api/authApi';
import { storage } from '@/utils/storage';
import {
  getTokenExpiryTime,
  isRefreshTokenUsable,
  isTokenUsable,
} from '../utils/jwtPermissions';

export interface AuthSession {
  accessToken: string;
  refreshToken: string | null;
  tokenExpiryTime: string;
}

let refreshPromise: Promise<AuthSession> | null = null;
let restorePromise: Promise<AuthSession | null> | null = null;

export const persistAuthentication = (result: LoginResult): AuthSession => {
  const tokenExpiryTime = getTokenExpiryTime(result.token);
  if (
    !tokenExpiryTime
    || !isTokenUsable(result.token)
    || !isRefreshTokenUsable(result.refreshToken)
  ) {
    throw new Error('The authentication server returned an invalid token pair.');
  }

  storage.setAuthSession(result.token, result.refreshToken, tokenExpiryTime);
  return {
    accessToken: result.token,
    refreshToken: result.refreshToken,
    tokenExpiryTime,
  };
};

export const clearAuthenticationSession = (): void => {
  storage.clearAuthSession();
};

const refreshStoredSession = async (): Promise<AuthSession> => {
  const refreshToken = storage.getRefreshToken();
  if (!refreshToken || !isRefreshTokenUsable(refreshToken)) {
    clearAuthenticationSession();
    throw new Error('The refresh token is missing or expired.');
  }

  try {
    const result = await refreshAuthentication(refreshToken);
    return persistAuthentication(result);
  } catch (error: unknown) {
    clearAuthenticationSession();
    throw error;
  }
};

export const refreshAuthenticationSession = (): Promise<AuthSession> => {
  if (!refreshPromise) {
    refreshPromise = refreshStoredSession().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const restoreStoredSession = async (): Promise<AuthSession | null> => {
  const accessToken = storage.getAccessToken();
  const refreshToken = storage.getRefreshToken();
  const tokenExpiryTime = getTokenExpiryTime(accessToken ?? '');

  if (accessToken && tokenExpiryTime && isTokenUsable(accessToken)) {
    const currentSession: AuthSession = {
      accessToken,
      refreshToken,
      tokenExpiryTime,
    };

    try {
      if (await introspectToken(accessToken)) return currentSession;
    } catch {
      // Keep a locally valid session during a temporary network/server outage.
      return currentSession;
    }
  }

  if (isRefreshTokenUsable(refreshToken)) {
    try {
      return await refreshAuthenticationSession();
    } catch {
      return null;
    }
  }

  clearAuthenticationSession();
  return null;
};

export const restoreAuthenticationSession = (): Promise<AuthSession | null> => {
  if (!restorePromise) {
    restorePromise = restoreStoredSession().finally(() => {
      restorePromise = null;
    });
  }
  return restorePromise;
};
